import dayjs, { Dayjs } from "dayjs"
import { AnimatePresence, motion } from "motion/react"
import { useMemo } from "react"

import Flex from "#blocks/Flex"
import { VALID_SWITCH_STATUSES } from "#constants/globalConstants"
import { ClassSessionConstructor } from "#customTypes/composedTypes/classSessionTypes"
import { AttendanceClassSessions } from "#customTypes/types"
import { getNow } from "#utils/dateHelpers"
import { remapAbsentForFutureSessions } from "#utils/studentHelpers"

import AddRemoveButtonRow from "./AddRemoveButtonRow"
import SessionGrid from "./SessionGrid"

type SessionMetadata = {
  disabledReason?: string
}

const EMPTY_ARRAY = [] as const

const ClassSessionSelectionGrid = ({
  attendanceForClass,
  classSessions,
  selectedSessions,
  setSelectedSessions,
  disabledMessage,
  headerActions,
  sessionMetadata,
  filterDate,
  newlySelectedSessions,
  droppedSessions,
}: {
  attendanceForClass: AttendanceClassSessions
  classSessions: ClassSessionConstructor[]
  selectedSessions: boolean[]
  setSelectedSessions: (sessions: boolean[]) => void
  disabledMessage?: string
  headerActions?: React.ReactNode
  sessionMetadata?: SessionMetadata[]
  filterDate?: Dayjs
  newlySelectedSessions?: boolean[]
  droppedSessions?: boolean[]
}) => {
  const now = filterDate || getNow().startOf("day")

  // Calculate which sessions are out of bounds (past) and which is the first session
  const sessionStates = useMemo(() => {
    if (!classSessions.length) return EMPTY_ARRAY

    let firstSessionIndex: number | null = null

    return classSessions.map((session, i) => {
      const sessionDate = dayjs(session.eventStart).startOf("day")
      const isBeforeFilterDate = sessionDate.isBefore(now, "day")

      // First session is the first one on or after the filter date
      if (firstSessionIndex === null && !isBeforeFilterDate) {
        firstSessionIndex = i
      }

      return {
        isPast: isBeforeFilterDate,
      }
    })
  }, [classSessions, now])

  const items = useMemo(() => {
    return (
      classSessions?.map((session, i) => {
        const originalStatus = attendanceForClass[i].status
        // Remap ABSENT to ENROLLED for future sessions
        const status = remapAbsentForFutureSessions(
          originalStatus,
          dayjs(session.eventStart),
          now
        )
        const state = sessionStates[i] || {
          isPast: false,
        }
        const metadata = sessionMetadata?.[i]

        const isValidStatus = VALID_SWITCH_STATUSES.includes(status)

        // Determine disabled reason
        let disabledReason: string | undefined = metadata?.disabledReason

        if (!metadata?.disabledReason && !isValidStatus) {
          disabledReason = "Session has already been attended"
        }

        return {
          session,
          sessionId: session.id,
          status,
          isSelected: !isValidStatus || selectedSessions[i],
          isNewlySelected: newlySelectedSessions?.[i] ?? false,
          isDropped: droppedSessions?.[i] ?? false,
          isDisabled: !isValidStatus,
          isPast: state.isPast,
          disabledReason,
          onClick: () => {
            // Block clicking if disabled
            if (!isValidStatus) return

            const newSelectedSessions = [...selectedSessions]
            newSelectedSessions[i] = !newSelectedSessions[i]
            setSelectedSessions(newSelectedSessions)
          },
          key: session.id,
        }
      }) || EMPTY_ARRAY
    )
  }, [
    classSessions,
    now,
    attendanceForClass,
    selectedSessions,
    newlySelectedSessions,
    droppedSessions,
    sessionMetadata,
    sessionStates,
    setSelectedSessions,
  ])

  return (
    <Flex col gap={10}>
      <h2>Sessions:</h2>
      <Flex
        style={{
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginRight: headerActions ? 12 : 0,
        }}
      >
        {disabledMessage && (
          <Flex style={{ marginBottom: 10 }}>{disabledMessage}</Flex>
        )}
        {headerActions && <Flex>{headerActions}</Flex>}
      </Flex>

      <AddRemoveButtonRow
        attendanceForClass={attendanceForClass}
        sortedClassSessions={classSessions}
        selectedSessions={selectedSessions}
        setSelectedSessions={setSelectedSessions}
      />

      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{
            ease: "easeOut",
            opacity: { duration: 0.7 },
            height: { duration: 0.2 },
          }}
          style={{ width: "fit-content" }}
        >
          <SessionGrid items={items} />
        </motion.div>
      </AnimatePresence>
    </Flex>
  )
}

export default ClassSessionSelectionGrid
